import { useEffect, useState } from "react";
import { CalendarViewChangeParams } from "primereact/calendar";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import UiInputText from "../input-text/InputText.component";
import UiSelect from "../select/Select.component";
import UiInputDate from "../input-date/InputDate.component";
import UiSelectButton from "../select-button/SelectButton.component";
import { Validations } from "../../services/form/validations.service";
import UiButton from "../button/Button";
import {
	AppointmentModalCoach,
	AppointmentModalFormData,
	AppointmentModalMode,
	AppointmentModalStoredData,
} from "../../models/components/appointment-modal.model";
import {
	Appointment,
	AppointmentCoach,
	AppointmentParticipant,
	AppointmentType,
	AppointmentTypes,
} from "../../models/entities/appointments.model";
import { Timezone } from "../../models/entities/participant.model";
import { List, ListOption } from "../../models/misc.model";
import { EndpointsService } from "../../services/endpoints/endpoints.service";
import { AxiosError } from "axios";
import UiFieldMessage from "../field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../models/components/field-message.model";
import { Formatter } from "../../services/formatter/formatter.service";
import { FormApi } from "final-form";
import { TimezoneService } from "../../services/timezone/timezone.service";
import FormSection from "../form-section/FormSection.component";
import { AppointmentsService } from "../../services/appointments/appointments.service";

const AppointmentModalForm = ({
	mode,
	participant,
	coach,
	appointment,
	appointmentCoaches,
	appointmentTypes,
	sharedData,
	onNext,
}: {
	mode: AppointmentModalMode;
	participant: AppointmentParticipant;
	coach: AppointmentModalCoach;
	appointment: Appointment | null;
	appointmentCoaches: AppointmentCoach[];
	appointmentTypes: AppointmentTypes[];
	sharedData: AppointmentModalStoredData | null;
	onNext: (e: AppointmentModalStoredData) => void;
}) => {
	const { t } = useTranslation("common");

	const [formData, setFormData] = useState<AppointmentModalFormData>({
		kannactId: sharedData?.formData?.kannactId ?? participant.kannactId ?? "",
		calendarId:
			sharedData?.formData?.calendarId ?? appointment?.coachId ?? coach.calendarId ?? null,
		appointmentType: sharedData?.formData?.appointmentType ?? appointment?.type ?? null,
		dateOn:
			sharedData?.formData?.dateOn ??
			(appointment?.datetime ? new Date(appointment.datetime) : null),
		timezone:
			sharedData?.formData?.timezone ??
			TimezoneService.getGs2Value(appointment?.participant.timezone) ??
			(participant.timezone && participant.timezone !== Timezone.NA
				? participant.timezone
				: null),
		time: sharedData?.formData?.time ?? appointment?.datetime ?? null,
	});

	// Availables dates
	const [disabledDates, setDisabledDates] = useState<Date[]>(sharedData?.disabledDates ?? []);
	const [datesMessage, setDatesMessage] = useState<{
		submitting: boolean;
		message: string | null;
	}>({
		submitting: false,
		message: null,
	});

	// Currently-viewed calendar month (1-based) + cache of months already fetched,
	// so paging to a future month fetches its availability lazily and paging back
	// reuses the cache instead of re-requesting.
	const initialViewDate = formData.dateOn ?? new Date();
	const [viewedMonth, setViewedMonth] = useState<{ year: number; month: number }>({
		year: initialViewDate.getFullYear(),
		month: initialViewDate.getMonth() + 1,
	});
	const [fetchedMonths, setFetchedMonths] = useState<Set<string>>(new Set());
	const monthKey = (year: number, month: number) => `${year}-${month}`;

	// Time slots
	const [timeSlots, setTimeSlots] = useState<List>(sharedData?.timeSlots ?? []);
	const [timeSlotsMessage, setTimeSlotsMessage] = useState<{
		submitting: boolean;
		message: string | null;
	}>({
		submitting: false,
		message: null,
	});

	// First time load
	useEffect(() => {
		if (formData.appointmentType && formData.calendarId && !sharedData?.disabledDates)
			getDates(viewedMonth.year, viewedMonth.month);
		if (formData.dateOn && !sharedData?.timeSlots) getTimeSlots(formData.dateOn);
	}, []);

	// When appointmentType & calendarId exists (get dates for the viewed month)
	useEffect(() => {
		if (formData.appointmentType && formData.calendarId && !sharedData?.disabledDates)
			getDates(viewedMonth.year, viewedMonth.month);
	}, [formData.appointmentType, formData.calendarId]);

	// Reset the availability cache: appointment type / calendar changed, so any
	// previously-fetched month is stale. The viewed month resets to today because
	// dateOn is cleared, and the dependency effect re-fetches it.
	const resetDatesCache = () => {
		const today = new Date();
		setDisabledDates([]);
		setFetchedMonths(new Set());
		setViewedMonth({ year: today.getFullYear(), month: today.getMonth() + 1 });
	};

	// Field changed: appointment type -> Update
	const appointmentTypeChanged = (
		value: AppointmentType,
		form: FormApi<AppointmentModalFormData, Partial<AppointmentModalFormData>>
	) => {
		// Update form
		if (form.getFieldState("dateOn")) form.resetFieldState("dateOn");
		if (form.getFieldState("time")) form.resetFieldState("time");
		setFormData({
			...formData,
			appointmentType: value,
			dateOn: null,
			time: null,
		});
		resetDatesCache();
	};

	const calendarIdChanged = (
		value: string,
		form: FormApi<AppointmentModalFormData, Partial<AppointmentModalFormData>>
	) => {
		// Update form
		if (form.getFieldState("dateOn")) form.resetFieldState("dateOn");
		if (form.getFieldState("time")) form.resetFieldState("time");
		setFormData({
			...formData,
			calendarId: value,
			dateOn: null,
			time: null,
		});
		resetDatesCache();
	};

	// Available dates: fetch availability for a single (year, month) — month is
	// 1-based — and merge that month's disabled days into the cache. Callers guard
	// against re-fetching an already-cached month.
	const getDates = async (year: number, month: number) => {
		// Update state
		setDatesMessage({ submitting: true, message: null });

		// Get dates
		await EndpointsService.appointments
			.getDates({
				config: {
					params: {
						year,
						month,
						appointmentType: formData.appointmentType!,
						coachId: AppointmentsService.getAppointmentCoach(
							appointmentCoaches,
							formData.calendarId!
						)!.id, // Calendar ID
						timezone: coach.timezone,
					},
				},
			})
			.then((response) => {
				// Build this month's disabled days and merge into the cache so
				// previously-fetched months stay disabled correctly.
				const monthDisabled = AppointmentsService.getDisabledDatesForMonth(
					year,
					month,
					response
				);
				setDisabledDates((prev) =>
					AppointmentsService.mergeDisabledDates(prev, monthDisabled)
				);
				setFetchedMonths((prev) => new Set(prev).add(monthKey(year, month)));
				setDatesMessage({ submitting: false, message: null });
			})
			.catch(() => {
				setDatesMessage({
					submitting: false,
					message: "APPOINTMENTS.HTTP_MESSAGES.AVAILABLE_DATES_500",
				});
			});
	};

	// Calendar paged to a different month: lazily fetch that month's availability
	// unless it is already cached.
	const viewDateChanged = (event: CalendarViewChangeParams) => {
		const viewDate = event.value;
		if (!viewDate) return;
		const year = viewDate.getFullYear();
		const month = viewDate.getMonth() + 1;
		setViewedMonth({ year, month });
		if (
			formData.appointmentType &&
			formData.calendarId &&
			!fetchedMonths.has(monthKey(year, month))
		) {
			getDates(year, month);
		}
	};

	// Field changed: date
	const dateOnChanged = async (
		date: Date | null,
		form: FormApi<AppointmentModalFormData, Partial<AppointmentModalFormData>>
	) => {
		// Set default states
		if (form.getFieldState("time")) form.resetFieldState("time");
		setFormData({
			...formData,
			time: null,
			dateOn: date,
		});
		setTimeSlots([]);
		setTimeSlotsMessage({ submitting: !!date, message: null });

		// Get available time slots
		if (date) getTimeSlots(date);
	};

	// Available time slots
	const getTimeSlots = async (date: Date) => {
		const year = `${date.getFullYear()}`;
		const month =
			date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : `${date.getMonth() + 1}`;
		const day = date.getDate() < 10 ? `0${date.getDate()}` : `${date.getDate()}`;
		await EndpointsService.appointments
			.getTimeSlots({
				config: {
					params: {
						appointmentType: formData.appointmentType!,
						coachId: AppointmentsService.getAppointmentCoach(
							appointmentCoaches,
							formData.calendarId!
						)!.id, // Calendar ID
						date: `${year}-${month}-${day}`,
						timezone: coach.timezone,
					},
				},
			})
			.then((response) => {
				setTimeSlotsMessage({ submitting: false, message: null });

				if (response.length > 0) {
					setTimeSlots(
						response
							.filter((x) => new Date(x.datetime).getTime() >= new Date().getTime())
							.map((x) => {
								const coachTime = Formatter.dateISOToString(x.datetime, {
									hour: "2-digit",
									minute: "2-digit",
									hour12: true,
									timeZoneName: "shortGeneric",
								});

								let ptTime: string | null = null;
								if (formData.timezone && formData.timezone !== Timezone.NA) {
									ptTime =
										Formatter.dateISOToString(x.datetime, {
											hour: "2-digit",
											minute: "2-digit",
											hour12: true,
											timeZoneName: "shortGeneric",
											timeZone: formData.timezone,
										}) ?? null;
								}

								return new ListOption({
									id: x.datetime,
									label: `${ptTime ?? Formatter.NO_DATA} / ${coachTime}`,
								});
							})
					);
				}
			})
			.catch((error: AxiosError) => {
				let errorMsg = "";
				if (error.response?.status === 400) {
					errorMsg = "UI_COMPONENTS.FIELD_MESSAGE.HTTP.400";
				} else {
					errorMsg =
						error.response?.status === 404
							? "APPOINTMENTS.HTTP_MESSAGES.TIME_SLOTS_404"
							: "APPOINTMENTS.HTTP_MESSAGES.TIME_SLOTS_500";
				}
				setTimeSlotsMessage({ submitting: false, message: errorMsg });
			});
	};

	// Field changed: participant's timezone --> Update time slots description
	const timezoneChanged = (timezone: string) => {
		if (timeSlots.length > 0) {
			// Update the time (participant's timezone)
			setTimeSlots(
				timeSlots.map((x) => {
					const coachTime = Formatter.dateISOToString(x.id as string, {
						hour: "2-digit",
						minute: "2-digit",
						hour12: true,
						timeZoneName: "shortGeneric",
					});
					const ptTime = Formatter.dateISOToString(x.id as string, {
						hour: "2-digit",
						minute: "2-digit",
						hour12: true,
						timeZoneName: "shortGeneric",
						timeZone: timezone,
					});
					x.label = `${ptTime} / ${coachTime}`;
					return x;
				})
			);
		}
	};

	const createAppointmentTypeLabel = (appointmentType: AppointmentTypes): string => {
		const label = t(`ENUMS.APPOINTMENTS_TYPE.${appointmentType.type}`);
		const duration = appointmentType.durationInMinutes ?? 0;
		const buffer = appointmentType.totalPaddingInMinutes ?? 0;
		return `${label} (${duration} min + ${buffer} buffer)`;
	};

	return (
		<Form
			onSubmit={() => onNext({ formData, disabledDates, timeSlots })}
			initialValues={formData}
			render={({ handleSubmit, form, submitting }) => (
				<form onSubmit={handleSubmit}>
					{/* Reschedule info message */}
					{mode === AppointmentModalMode.RESCHEDULE && (
						<p>
							{t("APPOINTMENTS.MODAL_APPOINTMENT.MSG_INFO", {
								date: Formatter.date(appointment?.datetime, {
									day: "2-digit",
									month: "long",
									year: "numeric",
									weekday: "long",
								}),
								ptTime: Formatter.dateISOToString(appointment?.datetime, {
									hour: "2-digit",
									minute: "2-digit",
									hour12: true,
									timeZoneName: "shortGeneric",
									timeZone: participant.timezone,
								}),
								coachTime: Formatter.dateISOToString(appointment?.datetime, {
									hour: "2-digit",
									minute: "2-digit",
									hour12: true,
									timeZoneName: "shortGeneric",
								}),
							})}
						</p>
					)}

					<FormSection>
						{/* Kannact ID */}
						<div className="row col-12 col-sm-12 col-md-8 col-lg-4">
							<UiInputText
								id="kannactId"
								label="APPOINTMENTS.MODAL_APPOINTMENT.FORM_FIELDS.kannactId"
								name="kannactId"
								onChange={(e) =>
									setFormData({ ...formData, kannactId: e.target.value })
								}
								disabled={true}
							/>
						</div>

						{/* Appointment type */}
						<div className="row col-12 col-sm-12 col-md-8 col-lg-6">
							<UiSelect
								id="appointmentType"
								label="APPOINTMENTS.MODAL_APPOINTMENT.FORM_FIELDS.appointmentType"
								name="appointmentType"
								onChange={(e) => appointmentTypeChanged(e.target.value, form)}
								disabled={submitting || mode === AppointmentModalMode.RESCHEDULE}
								validations={[Validations.required]}
								options={
									mode === AppointmentModalMode.RESCHEDULE
										? appointmentTypes.map((x) => {
												return new ListOption({
													id: x.type,
													label: createAppointmentTypeLabel(x),
												});
											})
										: appointmentTypes
												.filter(
													(x) =>
														x.type ===
															AppointmentType.KANNACT_INTRO_CALL ||
														x.type ===
															AppointmentType.KANNACT_WHOLE_HEALTH_INTRO_CALL ||
														x.type ===
															AppointmentType.TECH_SUPPORT_CALL ||
														x.type ===
															AppointmentType.COACHING_CALL_10 ||
														x.type === AppointmentType.COACHING_CALL ||
														x.type ===
															AppointmentType.COACHING_CALL_30_15 ||
														x.type ===
															AppointmentType.KANNACT_WARM_HANDOFF ||
														x.type ===
															AppointmentType.COACHING_CALL_45 ||
														x.type ===
															AppointmentType.IN_PERSON_COACHING_SESSION ||
														x.type ===
															AppointmentType.ZOOM_COACHING_SESSION ||
														x.type ===
															AppointmentType.NEW_PATIENT_CONSULTATION_30_15 ||
														x.type ===
															AppointmentType.NEW_PATIENT_CONSULTATION_45 ||
														x.type ===
															AppointmentType.NEW_PATIENT_CONSULTATION_45_PHONE ||
														x.type ===
															AppointmentType.FOLLOWUP_VISIT_20_10 ||
														x.type ===
															AppointmentType.FOLLOWUP_VISIT_30_10 ||
														x.type ===
															AppointmentType.FOLLOWUP_VISIT_30_10_PHONE ||
														x.type ===
															AppointmentType.COVERAGE_BENEFITS_REVIEW ||
														x.type ===
															AppointmentType.INFORMATIONAL_CALL ||
														x.type ===
															AppointmentType.INFORMATION_CALL_10_5 ||
														x.type ===
															AppointmentType.WELLNESS_CHECK_10_5 ||
														x.type ===
															AppointmentType.WELLNESS_CHECK_20_10 ||
														x.type ===
															AppointmentType.PREVISIT_SETUP_CALL ||
														x.type ===
															AppointmentType.PREVISIT_SETUP_CALL_30_10 ||
														x.type === AppointmentType.NURSE_VISIT ||
														x.type ===
															AppointmentType.PRIORITY_COACHING_CALL
												)
												.map(
													(x) =>
														new ListOption({
															id: x.type,
															label: createAppointmentTypeLabel(x),
														})
												)
												.sort((a, b) => {
													// sort by name asc
													if (a.label < b.label) return -1;
													if (a.label > b.label) return 1;
													return 0;
												})
								}
							/>
						</div>

						{/* Appointment coach / calendarId */}
						{formData.appointmentType && (
							<div className="row col-12 col-sm-12 col-md-8 col-lg-4">
								<UiSelect
									id="calendarId"
									label="APPOINTMENTS.MODAL_APPOINTMENT.FORM_FIELDS.calendarId"
									name="calendarId"
									onChange={(e) => calendarIdChanged(e.target.value, form)}
									disabled={
										submitting || mode === AppointmentModalMode.RESCHEDULE
									}
									validations={[Validations.required]}
									options={appointmentCoaches
										.filter(
											(obj1, i, arr) =>
												arr.findIndex((obj2) => obj2.id === obj1.id) === i
										)
										.map((x) => new ListOption({ id: x.id, label: x.name }))}
								/>
							</div>
						)}

						<div className="row">
							{/* Date */}
							<div className="col-12 col-sm-12 col-md-12 col-lg-6">
								{/* Messages: error */}
								{datesMessage.message && (
									<UiFieldMessage
										severity={FieldMessageSeverity.DANGER}
										label={datesMessage.message}
									/>
								)}

								{/* Messages: loading */}
								{datesMessage.submitting && (
									<UiFieldMessage
										severity={FieldMessageSeverity.LOADING}
										label="APPOINTMENTS.HTTP_MESSAGES.AVAILABLE_DATES_LOADING"
									/>
								)}

								{/* Calendar stays mounted while a month fetch is in
								    flight (only disabled) so paging to a future month
								    keeps its view; it is unmounted only on a terminal
								    error. */}
								{!datesMessage.message && formData.appointmentType && (
									<UiInputDate
										id="dateOn"
										label="APPOINTMENTS.MODAL_APPOINTMENT.FORM_FIELDS.dateOn"
										name="dateOn"
										onChange={(e) =>
											dateOnChanged(e.target.value as Date | null, form)
										}
										onViewDateChange={viewDateChanged}
										viewDate={
											new Date(viewedMonth.year, viewedMonth.month - 1, 1)
										}
										disabled={
											submitting ||
											datesMessage.submitting ||
											!formData.appointmentType
										}
										minDate={new Date()}
										disabledDates={disabledDates}
										validations={[Validations.required]}
										inline={true}
										showButtonBar={false}
									/>
								)}
							</div>

							{formData.dateOn && (
								<div className="col-12 col-sm-12 col-md-12 col-lg-6">
									{/* Participant's time zone */}
									<div className="row col-12 col-sm-12 col-md-12 col-lg-7">
										<UiSelect
											id="timezone"
											label="APPOINTMENTS.MODAL_APPOINTMENT.FORM_FIELDS.timezone"
											name="timezone"
											onChange={(e) => {
												setFormData({
													...formData,
													timezone: e.target.value,
												});
												timezoneChanged(e.target.value);
											}}
											disabled={submitting}
											validations={[Validations.required]}
											options={[
												{
													id: Timezone.US_Alaska,
													label: `ENUMS.TIMEZONES.US_Alaska`,
												},
												{
													id: Timezone.US_Arizona,
													label: `ENUMS.TIMEZONES.US_Arizona`,
												},
												{
													id: Timezone.US_Central,
													label: `ENUMS.TIMEZONES.US_Central`,
												},
												{
													id: Timezone.US_Eastern,
													label: `ENUMS.TIMEZONES.US_Eastern`,
												},
												{
													id: Timezone.US_Hawaii,
													label: `ENUMS.TIMEZONES.US_Hawaii`,
												},
												{
													id: Timezone.US_Mountain,
													label: `ENUMS.TIMEZONES.US_Mountain`,
												},
												{
													id: Timezone.US_Pacific,
													label: `ENUMS.TIMEZONES.US_Pacific`,
												},
											]}
										/>
									</div>

									{/* Time */}
									<div className="row col-12 col-sm-12 col-md-12 col-lg-10 time-slots-container">
										{/* Messages: error */}
										{timeSlotsMessage.message && (
											<UiFieldMessage
												severity={FieldMessageSeverity.DANGER}
												label={timeSlotsMessage.message}
											/>
										)}

										{/* Messages: loading */}
										{timeSlotsMessage.submitting && (
											<UiFieldMessage
												severity={FieldMessageSeverity.LOADING}
												label={
													"APPOINTMENTS.HTTP_MESSAGES.TIME_SLOTS_LOADING"
												}
											/>
										)}

										{/* Messages: no data */}
										{!timeSlotsMessage.message &&
										!timeSlotsMessage.submitting &&
										timeSlots.length === 0 ? (
											<UiFieldMessage
												severity={FieldMessageSeverity.INFO}
												label={
													"APPOINTMENTS.HTTP_MESSAGES.TIME_SLOTS_EMPTY"
												}
											/>
										) : (
											<UiSelectButton
												id="time"
												className="vertical"
												name="time"
												onChange={(e) =>
													setFormData({ ...formData, time: e.value })
												}
												options={timeSlots}
												validations={[Validations.required]}
											/>
										)}
									</div>
								</div>
							)}
						</div>
					</FormSection>

					{/* Actions: Next (confirmation) */}
					<div className="action-buttons">
						<UiButton
							id="modalNextBtn"
							label="UI_COMPONENTS.BUTTONS.NEXT"
							className="p-button-rounded"
							type="submit"
							disabled={
								timeSlotsMessage.submitting ||
								!form.getState().valid ||
								!formData.time
							}
						></UiButton>
					</div>
				</form>
			)}
		/>
	);
};

export default AppointmentModalForm;
